/**
 * Dialog para atribuir etiquetas a uma conversa
 * 
 * Permite selecionar múltiplas etiquetas para uma conversa.
 */

import { useState, useEffect } from 'react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { Loader2, Tag } from 'lucide-react';
import { Label, getLabelColorHex } from '@/hooks/useLabels';
import { cn } from '@/lib/utils';

interface AssignLabelsDialogProps {
  /** Se o dialog está aberto */
  open: boolean;
  /** Callback para fechar */
  onOpenChange: (open: boolean) => void;
  /** Etiquetas disponíveis */
  availableLabels: Label[];
  /** IDs das etiquetas atualmente atribuídas */
  currentLabelIds: string[];
  /** Callback ao salvar */
  onSave: (labelIds: string[]) => Promise<void>;
  /** Se está salvando */
  isSaving?: boolean;
  /** Nome do contato/conversa (para exibição) */
  conversationName?: string;
}

export function AssignLabelsDialog({
  open,
  onOpenChange,
  availableLabels,
  currentLabelIds,
  onSave,
  isSaving = false,
  conversationName,
}: AssignLabelsDialogProps) {
  // Estado local das seleções
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());

  // Sincroniza com props quando abre
  useEffect(() => {
    if (open) {
      setSelectedIds(new Set(currentLabelIds));
    }
  }, [open, currentLabelIds]);

  // Toggle de seleção
  const handleToggle = (labelId: string) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(labelId)) {
        next.delete(labelId);
      } else {
        next.add(labelId);
      }
      return next;
    });
  };

  // Handler de salvar
  const handleSave = async () => {
    await onSave(Array.from(selectedIds));
    onOpenChange(false);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[400px]">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Tag className="h-5 w-5" />
            Gerenciar Etiquetas
          </DialogTitle>
          <DialogDescription>
            {conversationName 
              ? `Selecione as etiquetas para "${conversationName}".`
              : 'Selecione as etiquetas para esta conversa.'}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-2 max-h-[300px] overflow-y-auto py-2">
          {availableLabels.length === 0 ? (
            <p className="text-center text-muted-foreground py-8">
              Nenhuma etiqueta disponível. Crie etiquetas primeiro.
            </p>
          ) : (
            availableLabels.map((label) => {
              const colorHex = label.color_hex || getLabelColorHex(String(label.color_index || 0));
              const isSelected = selectedIds.has(label.id);

              return (
                <div
                  key={label.id}
                  className={cn(
                    'flex items-center gap-3 p-3 rounded-lg border cursor-pointer transition-colors',
                    isSelected 
                      ? 'border-primary bg-primary/5' 
                      : 'hover:bg-muted/50'
                  )}
                  onClick={() => handleToggle(label.id)}
                >
                  <Checkbox
                    checked={isSelected}
                    onCheckedChange={() => handleToggle(label.id)}
                  />
                  <div
                    className="w-3 h-3 rounded-full flex-shrink-0"
                    style={{ backgroundColor: colorHex }}
                  />
                  <span className="text-sm font-medium flex-1 truncate">
                    {label.name}
                  </span>
                </div>
              );
            })
          )}
        </div>

        <DialogFooter>
          <Button
            variant="outline"
            onClick={() => onOpenChange(false)}
            disabled={isSaving}
          >
            Cancelar
          </Button>
          <Button onClick={handleSave} disabled={isSaving}>
            {isSaving && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
            Salvar
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
